// =====================================================================
// ReportsUploadPage.tsx  (route: /admin/ReportsUpload — Global Admin only)
// ---------------------------------------------------------------------
// Unified director kit-upload page. A picker chooses the kit type:
//   - Dashboards   → /api/admin/reportsImport/*   (util/reports/business)
//   - Result PDFs  → /api/admin/directorImport/*  (util/reports/dtc)
//
// The first was labelled "Business Reports" until 2026-07-30, from when that
// kit produced PDF reports. It ships React dashboards now; only the label
// changed, not the routes or the namespace.
// Both flows are identical (GCS upload → orchestration Cloud Build →
// import → deploy); only the endpoints + copy differ. Replaces the old
// separate "PDF Upload" + "Reports Upload" pages.
//
// Global-Admin only (both backends enforce isUserGlobalAdmin). Collision
// guard: polls <base>/status and DISABLES upload while a deploy for this
// env is running; a "notified the dev team" checkbox is required. Both are
// re-checked server-side.
// =====================================================================

import React, { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import Overlay from "../Overlay";
import { sweetalert } from "../../App";
import AdminAPI from "../../network/AdminAPI";

const TERMINAL_STATUSES = ["SUCCESS", "FAILURE", "INTERNAL_ERROR", "TIMEOUT", "CANCELLED", "EXPIRED"];

type KitKind = "reports" | "pdf";

interface DevStatus {
    success: boolean;
    configured: boolean;
    targetLabel: string;
    targetBranch: string;
    reviewMode: boolean;
    devBuildRunning: boolean;
    running: { trigger: string; id: string; status: string }[];
    canUpload: boolean;
    reason?: string;
}

interface BuildStatus {
    success: boolean;
    id?: string;
    status?: string;
    logUrl?: string;
    reason?: string;
}

// Per-kind API + copy. Both API trios share the same request/response shape.
const KINDS: Record<KitKind, {
    label: string;
    noun: string;
    status: () => Promise<DevStatus>;
    upload: (file: Blob, ack: boolean) => Promise<any>;
    build: (id: string) => Promise<BuildStatus>;
}> = {
    reports: {
        // Display text only. The KitKind key stays "reports" and so do the
        // routes (/api/admin/reportsImport/*) and the backend namespace
        // (util/reports/business) — renaming those would be a much wider
        // change for no benefit. What the director uploads here is dashboards.
        label: "Dashboards",
        noun: "dashboards kit",
        status: () => AdminAPI.reportsImportStatus(),
        upload: (file, ack) => AdminAPI.reportsKitUpload(file, ack),
        build: (id) => AdminAPI.reportsBuildStatus(id),
    },
    pdf: {
        label: "Result PDFs",
        noun: "result-PDF renderer kit",
        status: () => AdminAPI.directorImportStatus(),
        upload: (file, ack) => AdminAPI.pdfUpload(file, ack),
        build: (id) => AdminAPI.directorBuildStatus(id),
    },
};

export function ReportsUploadPage() {
    const [kind, setKind] = useState<KitKind>("reports");
    const [statusLoading, setStatusLoading] = useState(true);
    const [devStatus, setDevStatus] = useState<DevStatus | null>(null);
    const [file, setFile] = useState<File | null>(null);
    const [acknowledged, setAcknowledged] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [buildId, setBuildId] = useState<string | null>(null);
    const [build, setBuild] = useState<BuildStatus | null>(null);
    const [runBranch, setRunBranch] = useState<string | null>(null);
    const [runReview, setRunReview] = useState<boolean>(false);

    const cfg = KINDS[kind];

    const fetchStatus = useCallback(async () => {
        try {
            const s = await KINDS[kind].status();
            setDevStatus(s);
        } catch (e) {
            console.error("import status poll failed", e);
        } finally {
            setStatusLoading(false);
        }
    }, [kind]);

    useEffect(() => {
        document.title = "Reports Upload";
        fetchStatus();
        const iv = setInterval(fetchStatus, 8000);
        return () => clearInterval(iv);
    }, [fetchStatus]);

    useEffect(() => {
        if (!buildId) return;
        let cancelled = false;
        const poll = async () => {
            try {
                const b = await KINDS[kind].build(buildId);
                if (cancelled) return;
                setBuild(b);
                if (b.status && TERMINAL_STATUSES.includes(b.status)) clearInterval(iv);
            } catch (e) {
                console.error("build status poll failed", e);
            }
        };
        poll();
        const iv = setInterval(poll, 8000);
        return () => { cancelled = true; clearInterval(iv); };
    }, [buildId, kind]);

    // Switching kit type resets everything and re-checks the new target's guard.
    const switchKind = (k: KitKind) => {
        if (k === kind) return;
        setKind(k);
        setFile(null);
        setAcknowledged(false);
        setBuildId(null);
        setBuild(null);
        setDevStatus(null);
        setStatusLoading(true);
    };

    const onDrop = useCallback((accepted: File[]) => {
        if (accepted && accepted.length) setFile(accepted[0]);
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        maxFiles: 1,
        accept: [".tar.gz", ".tgz", ".gz", "application/gzip", "application/x-gzip"],
    });

    const statusError = !!devStatus && devStatus.success === false;
    const notConfigured = !!devStatus && devStatus.configured === false;
    const devBuildRunning = !!devStatus && devStatus.devBuildRunning === true;
    const canSubmit = !!file && acknowledged && !uploading && devStatus?.canUpload === true;
    const targetLabel = devStatus?.targetLabel || "target";
    const targetBranch = devStatus?.targetBranch;
    const reviewMode = devStatus?.reviewMode === true;

    const doUpload = async () => {
        if (!file) return;
        setUploading(true);
        try {
            const res = await cfg.upload(file, acknowledged);
            if (!res.success) {
                fetchStatus();
                sweetalert.fire({ icon: "error", title: "", text: res.reason || "Upload failed." });
                return;
            }
            setBuildId(res.buildId || null);
            setBuild(null);
            setRunBranch(res.pushBranch || null);
            setRunReview(res.reviewMode === true);
            setFile(null);
            setAcknowledged(false);
            sweetalert.fire({ icon: "success", title: "", text: "Import started. Watch progress below." });
        } catch (e) {
            console.error("kit upload failed", e);
            sweetalert.fire({ icon: "error", title: "", text: "Upload failed. Please try again." });
        } finally {
            setUploading(false);
        }
    };

    const renderBuildProgress = () => {
        if (!buildId) return null;
        const status = build?.status;
        let cls = "alert alert-info";
        let text = "Starting import…";
        const branch = runBranch || targetBranch || "the target branch";
        if (status === "QUEUED" || status === "PENDING") {
            text = "Queued…";
        } else if (status === "WORKING") {
            text = runReview
                ? `Importing the kit and pushing review branch ${branch}…`
                : `Importing the kit and pushing to ${branch}…`;
        } else if (status === "SUCCESS") {
            cls = "alert alert-success";
            text = runReview
                ? `Imported and pushed review branch ${branch}. Open a pull request from it into ${targetBranch || "main"} and merge to deploy to ${targetLabel}. Nothing has deployed yet.`
                : `Import pushed to ${branch}. The ${targetLabel} pipeline is now deploying — watch for the deploy emails.`;
        } else if (status === "FAILURE" || status === "INTERNAL_ERROR" || status === "TIMEOUT") {
            cls = "alert alert-danger";
            text = "Import FAILED — nothing was deployed. Check the build log (often a disallowed file in the kit or a compile/test failure).";
        } else if (status === "CANCELLED" || status === "EXPIRED") {
            cls = "alert alert-warning";
            text = "Build was cancelled.";
        }
        return (
            <div className="mt-3">
                <h5 className="verlag-bold">Import progress</h5>
                <div className={cls} role="status">
                    <p className="m-0">{text}</p>
                    {status && <p className="m-0 mt-1"><small>Build status: {status}</small></p>}
                </div>
                {build?.logUrl &&
                    <a href={build.logUrl} target="_blank" rel="noopener noreferrer">View build log</a>}
            </div>
        );
    };

    const renderGuard = () => {
        if (statusLoading) {
            return <div className="alert alert-secondary m-0"><p className="m-0">Checking for in-progress deploys…</p></div>;
        }
        if (notConfigured) {
            return <div className="alert alert-danger m-0"><p className="m-0">{cfg.label} import is not configured on this environment. Contact the dev team.</p></div>;
        }
        if (statusError) {
            return <div className="alert alert-danger m-0" role="alert"><p className="m-0">Couldn't read the deploy status ({devStatus?.reason || "server error"}). Upload is disabled until this can be verified.</p></div>;
        }
        if (devBuildRunning) {
            const n = devStatus?.running?.length || 0;
            return (
                <div className="alert alert-danger m-0" role="alert">
                    <p className="m-0">
                        A {targetLabel} deploy is currently running ({n} build{n === 1 ? "" : "s"}). Upload is disabled
                        until it finishes to avoid colliding with an in-progress deploy.
                    </p>
                </div>
            );
        }
        return <div className="alert alert-success m-0" role="status"><p className="m-0">No {targetLabel} deploy is running — safe to upload.</p></div>;
    };

    return (
        <React.Fragment>
            <Overlay show_loading={uploading} />
            <div className="container-fluid min-vh-100">
                <div className="row">
                    <div className="col-12 col-md-12 col-lg-10 col-xl-7 pt-2">
                        <main id="main-content" tabIndex={-1} aria-label="Reports Upload">
                            <div className="card mb-2">
                                <div className="card-header verlag-bold">
                                    <h4>Reports Upload</h4>
                                </div>
                                <div className="card-body">

                                    {/* Kit-type picker */}
                                    <div className="form-group">
                                        <label className="verlag-bold d-block">Kit type</label>
                                        <div className="btn-group" role="group" aria-label="Kit type">
                                            {(Object.keys(KINDS) as KitKind[]).map((k) => (
                                                <button
                                                    key={k}
                                                    type="button"
                                                    className={`btn ${kind === k ? "immySubmitButtonOutline" : "btn-outline-secondary"}`}
                                                    aria-pressed={kind === k}
                                                    onClick={() => switchKind(k)}
                                                >
                                                    {KINDS[k].label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {reviewMode ? (
                                        <div className="alert alert-info" role="alert">
                                            <p className="m-0">
                                                Uploading a {cfg.noun} for <strong>{targetLabel}</strong> imports the new
                                                templates and pushes a <strong>review branch</strong> (nothing is pushed to{" "}
                                                <code>{targetBranch || "main"}</code>). It does <strong>NOT</strong> deploy —
                                                the dev team opens a pull request from that branch and merges it to release
                                                to {targetLabel}.
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="alert alert-warning" role="alert">
                                            <p className="m-0">
                                                Uploading a {cfg.noun} deploys the new templates to the{" "}
                                                <strong>{targetLabel}</strong> environment
                                                {targetBranch ? <> (a push to <code>{targetBranch}</code>)</> : null}.
                                                {" "}This shares the branch with the development team, so only upload when no
                                                {" "}{targetLabel} deploy is running and the team has been notified.
                                            </p>
                                        </div>
                                    )}

                                    <div className="mb-3">{renderGuard()}</div>

                                    <div
                                        {...getRootProps()}
                                        style={{
                                            border: "2px dashed #ced4da",
                                            borderRadius: 6,
                                            padding: "2rem",
                                            textAlign: "center",
                                            cursor: "pointer",
                                            backgroundColor: isDragActive ? "#eef2ff" : "#f8f9fa",
                                        }}
                                    >
                                        <input aria-label={`${cfg.label} kit dropzone`} {...getInputProps()} />
                                        <p className="m-0">Drag &amp; drop the {cfg.noun} (<code>.tar.gz</code>) here, or click to select.</p>
                                    </div>

                                    {file &&
                                        <div className="mt-2">
                                            <strong>Selected:</strong> {file.name} ({file.size} bytes)
                                            <button type="button" className="btn btn-sm btn-link" onClick={() => setFile(null)}>clear</button>
                                        </div>}

                                    <div className="form-check mt-3">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={acknowledged}
                                            id="notifiedDevTeamCheck"
                                            onChange={() => setAcknowledged(!acknowledged)}
                                        />
                                        <label className="form-check-label" htmlFor="notifiedDevTeamCheck">
                                            I have notified the development team that I'm deploying to {targetLabel}.
                                        </label>
                                    </div>

                                </div>
                                <div className="card-footer">
                                    <button
                                        type="button"
                                        className="btn btn-primary float-right"
                                        disabled={!canSubmit}
                                        onClick={doUpload}
                                    >
                                        {uploading ? "Uploading…" : reviewMode ? `Upload for ${targetLabel} Review` : `Upload & Deploy to ${targetLabel}`}
                                    </button>
                                    <div style={{ clear: "both" }} />
                                </div>
                            </div>

                            {renderBuildProgress()}
                        </main>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
}
